import { Helmet } from 'react-helmet-async';

const SEO = ({
  title = 'Pharmaceutical Database - Comprehensive Drug & Clinical Trial Information',
  description = 'Access comprehensive pharmaceutical data from FDA, ClinicalTrials.gov, ChEBI, ChEMBL, PubChem, RxNorm, and DailyMed. Search drugs, clinical trials, and chemical compounds.',
  keywords = 'pharmaceutical database, FDA drugs, clinical trials, drug information, ChEBI, ChEMBL, PubChem, RxNorm, DailyMed, Orange Book, drug search, medical research',
  canonical = '',
  type = 'website',
  image = '/logo512.png',
  article = null,
  structuredData = null
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'http://localhost:3000';
  const fullUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Pharmaceutical Database',
    url: siteUrl,
    description: 'Comprehensive pharmaceutical information from authoritative government sources',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/drugs?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pharmaceutical Database',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo512.png`
      }
    }
  };

  const finalStructuredData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Pharmaceutical Database" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime || article.publishedTime} />
          <meta property="article:author" content={article.author || 'Pharmaceutical Database'} />
          <meta property="article:section" content={article.section || 'Health'} />
          {article.tags && article.tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="Pharmaceutical Database" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(finalStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
